import * as React from "react";
import { Clock } from "lucide-react";

import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

function useMediaQuery(query) {
  const [matches, setMatches] = React.useState(false);

  React.useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;

    const media = window.matchMedia(query);
    const update = () => setMatches(Boolean(media.matches));

    update();
    if (media.addEventListener) {
      media.addEventListener("change", update);
      return () => media.removeEventListener("change", update);
    }

    media.addListener(update);
    return () => media.removeListener(update);
  }, [query]);

  return matches;
}

function pad2(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "00";
  return String(n).padStart(2, "0");
}

function toHHmm(value) {
  if (!value) return "";
  const raw = String(value).trim();
  if (!raw) return "";

  // Accept HH:mm and HH:mm:ss (also allows single-digit hours).
  const match = /^(\d{1,2}):(\d{2})(?::\d{2})?$/.exec(raw);
  if (!match) return raw;

  const h = Math.min(23, Math.max(0, Number(match[1])));
  const m = Math.min(59, Math.max(0, Number(match[2])));
  return `${pad2(h)}:${pad2(m)}`;
}

function parseHHmm(value) {
  const hhmm = toHHmm(value);
  const match = /^(\d{2}):(\d{2})$/.exec(hhmm);
  if (!match) return null;
  return { hour: match[1], minute: match[2] };
}

function buildMinuteOptions(step, currentMinute) {
  const safeStep = Math.max(1, Number(step) || 1);
  const options = [];
  for (let minute = 0; minute < 60; minute += safeStep) {
    options.push(pad2(minute));
  }

  if (currentMinute && !options.includes(currentMinute)) {
    options.push(currentMinute);
    options.sort((a, b) => Number(a) - Number(b));
  }

  return options;
}

/**
 * TimePicker
 * - Mobile (touch / small screens): native <input type="time">
 * - Desktop: custom popover with Hour + Minute selects
 */
function TimePicker({
  value,
  onChange,
  onBlur,
  name,
  disabled,
  className,
  minuteStep = 5,
  placeholder = "Select time",
  useNativeOnMobile = true,
}) {
  const preferNative = useMediaQuery("(pointer: coarse), (max-width: 768px)");
  const shouldUseNative = useNativeOnMobile && preferNative;

  const normalizedValue = toHHmm(value);
  const parsed = parseHHmm(normalizedValue);

  const [open, setOpen] = React.useState(false);
  const [hour, setHour] = React.useState(parsed?.hour || "09");
  const [minute, setMinute] = React.useState(parsed?.minute || "00");

  React.useEffect(() => {
    if (!open) return;
    const next = parseHHmm(toHHmm(value));
    setHour(next?.hour || "09");
    setMinute(next?.minute || "00");
  }, [open, value]);

  const hours = React.useMemo(
    () => Array.from({ length: 24 }, (_, i) => pad2(i)),
    []
  );

  const minutes = React.useMemo(
    () => buildMinuteOptions(minuteStep, parsed?.minute),
    [minuteStep, parsed?.minute]
  );

  const emit = React.useCallback(
    (nextHour, nextMinute) => {
      const hh = pad2(nextHour);
      const mm = pad2(nextMinute);
      onChange?.(`${hh}:${mm}`);
    },
    [onChange]
  );

  if (shouldUseNative) {
    return (
      <Input
        type="time"
        name={name}
        disabled={disabled}
        value={normalizedValue}
        onChange={(e) => onChange?.(toHHmm(e.target.value))}
        onBlur={onBlur}
        className={className}
      />
    );
  }

  return (
    <Popover
      open={open}
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen);
        if (!nextOpen) onBlur?.();
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          className={cn(
            "text-(--neutral-700) bg-(--neutral-50) border border-(--neutral-300) rounded-md px-3 py-1 text-base shadow-xs transition-[color,box-shadow] outline-none h-9 w-full min-w-0 md:text-sm",
            "flex items-center justify-between text-left",
            "focus-visible:border-(--color-ring) focus-visible:ring-(--color-ring)/50 focus-visible:ring-[3px]",
            "aria-invalid:ring-(--color-destructive)/20 dark:aria-invalid:ring-(--color-destructive)/40 aria-invalid:border-(--color-destructive)",
            "disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50",
            className
          )}
          aria-label={name || "time"}
        >
          <span
            className={cn(
              "truncate",
              !normalizedValue && "text-(--color-muted-foreground)"
            )}
          >
            {normalizedValue || placeholder}
          </span>
          <Clock className="h-4 w-4 shrink-0 text-(--neutral-700)" />
        </button>
      </PopoverTrigger>

      <PopoverContent
        align="start"
        side="bottom"
        sideOffset={6}
        className="w-70 p-3 border border-(--neutral-300) bg-(--surface-base)"
      >
        <div className="grid grid-cols-2 gap-2">
          <div className="space-y-1">
            <div className="text-xs font-body text-(--neutral-700)">Hour</div>
            <Select
              value={hour}
              onValueChange={(h) => {
                setHour(h);
                emit(h, minute);
              }}
              disabled={disabled}
            >
              <SelectTrigger className="w-full border border-(--neutral-300) text-(--neutral-800)">
                <SelectValue placeholder="HH" />
              </SelectTrigger>
              <SelectContent
                align="start"
                className="bg-(--surface-base) border border-(--neutral-300) text-(--neutral-800)"
              >
                {hours.map((h) => (
                  <SelectItem
                    key={h}
                    value={h}
                    className="text-(--neutral-800)"
                    onPointerDown={() => {
                      // Radix Select won't fire onValueChange if selecting the same value.
                      // Emit anyway so defaults like 09:00 are actually selectable.
                      if (h === hour) emit(h, minute);
                    }}
                  >
                    {h}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1">
            <div className="text-xs font-body text-(--neutral-700)">Minute</div>
            <Select
              value={minute}
              onValueChange={(m) => {
                setMinute(m);
                emit(hour, m);
              }}
              disabled={disabled}
            >
              <SelectTrigger className="w-full border border-(--neutral-300) text-(--neutral-800)">
                <SelectValue placeholder="MM" />
              </SelectTrigger>
              <SelectContent
                align="start"
                className="bg-(--surface-base) border border-(--neutral-300) text-(--neutral-800)"
              >
                {minutes.map((m) => (
                  <SelectItem
                    key={m}
                    value={m}
                    className="text-(--neutral-800)"
                    onPointerDown={() => {
                      // Same-value selection doesn't trigger onValueChange in Radix Select.
                      if (m === minute) emit(hour, m);
                    }}
                  >
                    {m}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="mt-3 flex items-center justify-between">
          <button
            type="button"
            className="text-sm font-body text-(--neutral-700) hover:text-(--neutral-900)"
            onClick={() => {
              onChange?.("");
              setOpen(false);
            }}
            disabled={disabled}
          >
            Clear
          </button>
          <button
            type="button"
            className="text-sm font-body text-(--color-primary) hover:opacity-90"
            onClick={() => {
              const now = new Date();
              const hh = pad2(now.getHours());
              const mm = pad2(now.getMinutes());
              setHour(hh);
              setMinute(mm);
              onChange?.(`${hh}:${mm}`);
              setOpen(false);
            }}
            disabled={disabled}
          >
            Now
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export { TimePicker };
